import {createSlice} from '@reduxjs/toolkit';

import storeSliceNames from '../../consts/storeSliceNames';
import helpers from '../../utils/helpers';

const initialState = {
    currentDusn: undefined,
    // true = picked by sorting, may be re-picked. false = chosen on purpose.
    isCurrentDusnAutoSelected: false,
    deviceList: {},
    ltePairingError: '',
};

const iotDevicesSlice = createSlice({
    name: storeSliceNames.iotDevices,
    initialState,
    reducers: {
        setDevice(state, action) {
            const device = action.payload;

            state.deviceList[device.serialNumber] = device;
        },
        updateDeviceData(state, action) {
            const {dusn, data} = action.payload;

            if (!dusn || !data) return;

            const currentData = state.deviceList[dusn];

            if (currentData) {
                const mergedData = helpers.mergeDeep(currentData, data);

                state.deviceList[dusn] = currentData ? mergedData : data;
            }
        },
        updateDeviceFirmwareData(state, action) {
            const {dusn, data} = action.payload;

            if (!dusn || !data) return;

            if (state.deviceList[dusn]) {
                state.deviceList[dusn].firmware = {...(state?.deviceList?.[dusn]?.firmware || {}), ...data};
            }
        },
        resetDeviceFirmwareData(state, action) {
            const {dusn} = action.payload;

            if (!dusn) return;

            if (state.deviceList[dusn]) {
                state.deviceList[dusn].firmware = {};
            }
        },
        removeDevice(state, action) {
            delete state.deviceList[action.payload];
        },
        setDeviceConnectionStatus(state, action) {
            const {dusn, status} = action.payload;

            if (state.deviceList[dusn]) {
                state.deviceList[dusn].connectionStatus = status;
                state.deviceList[dusn].lastConnectionStatusUpdateDate = Date.now();
            }
        },
        setIsDeviceAssetsReceived(state, action) {
            const {dusn, value} = action.payload;

            if (state.deviceList[dusn]) {
                state.deviceList[dusn].isDeviceAssetsReceived = value;
            }
        },
        setIsDeviceAssetsInProgress(state, action) {
            const {dusn, value} = action.payload;

            if (state.deviceList[dusn]) {
                state.deviceList[dusn].isDeviceAssetsInProgress = value;
            }
        },
        setIsDeviceConnectInProgress(state, action) {
            const {dusn, value} = action.payload;

            if (state.deviceList[dusn]) {
                state.deviceList[dusn].isDeviceConnectInProgress = value;
            }
        },
        setIsDeviceUpdateSettingInProgress(state, action) {
            const {dusn, settingName, value} = action.payload;

            if (state.deviceList[dusn]) {
                let settingsArray = state.deviceList[dusn].deviceUpdateSettingsInProgress || [];

                if (value) {
                    if (!settingsArray.includes(settingName)) {
                        settingsArray.push(settingName);
                    }
                } else {
                    settingsArray = settingsArray.filter((x) => x !== settingName);
                }

                state.deviceList[dusn].deviceUpdateSettingsInProgress = settingsArray;
            }
        },
        setIsDeviceSyncInProgress(state, action) {
            const {dusn, value} = action.payload;

            if (state.deviceList[dusn]) {
                state.deviceList[dusn].isDeviceSyncInProgress = value;
            }
        },
        setIsDeviceSyncIsFailed(state, action) {
            const {dusn, value} = action.payload;

            if (state.deviceList[dusn]) {
                state.deviceList[dusn].isDeviceSyncIsFailed = value;
            }
        },
        setIsDeviceSyncStarted(state, action) {
            const {dusn, value} = action.payload;

            if (state.deviceList[dusn]) {
                state.deviceList[dusn].isDeviceSyncStarted = value;
            }
        },
        setIsDeviceConnectIsFailed(state, action) {
            const {dusn, value} = action.payload;

            if (state.deviceList[dusn]) {
                state.deviceList[dusn].isDeviceConnectIsFailed = value;
            }
        },
        setIsDeviceInitialized(state, action) {
            const {dusn, value} = action.payload;

            if (state.deviceList[dusn]) {
                state.deviceList[dusn].isDeviceInitialized = value;
            }
        },
        setIsDeviceReconnectInProgress(state, action) {
            const {dusn, value} = action.payload;

            if (state.deviceList[dusn]) {
                state.deviceList[dusn].isDeviceReconnectInProgress = value;
            }
        },
        setDeviceAuthToken(state, action) {
            const {dusn, value} = action.payload;

            if (state.deviceList[dusn]) {
                state.deviceList[dusn].authToken = value;
            }
        },
        clearDevices(state) {
            state.deviceList = {};
        },
        setCurrentDusn(state, action) {
            state.currentDusn = action.payload;
            state.isCurrentDusnAutoSelected = false;
        },
        autoSelectCurrentDusn(state, action) {
            state.currentDusn = action.payload;
            state.isCurrentDusnAutoSelected = true;
        },
        setLtePairingError(state, action) {
            state.ltePairingError = action.payload;
        },
        setExperienceInferenceData(state, action) {
            const {dusn, data} = action.payload;

            if (state.deviceList[dusn]) {
                state.deviceList[dusn].experience = data;
            }
        },
        setExperienceCalibrationData(state, action) {
            const {dusn, data} = action.payload;

            if (state.deviceList[dusn]) {
                if (!state.deviceList[dusn].experience) state.deviceList[dusn].experience = {};

                state.deviceList[dusn].experience.calibration = data;
            }
        },
    },
});

export const setDevice = (device) => iotDevicesSlice.actions.setDevice(device);

export const updateDeviceData = (dusn, data) => iotDevicesSlice.actions.updateDeviceData({dusn, data});

export const updateDeviceSettings = (dusn, settingName, settingValue) =>
    updateDeviceData(dusn, {settings: {[settingName]: settingValue}});

export const updateDeviceFirmwareData = (dusn, data) => iotDevicesSlice.actions.updateDeviceFirmwareData({dusn, data});

export const resetDeviceFirmwareData = (dusn) => iotDevicesSlice.actions.resetDeviceFirmwareData({dusn});

export const setIsAutomaticFWInstallationActive = (dusn, isAutomaticFWInstallationActive) =>
    updateDeviceFirmwareData(dusn, {isAutomaticFWInstallationActive});

export const removeDevice = (dusn) => iotDevicesSlice.actions.removeDevice(dusn);

export const clearDevices = () => iotDevicesSlice.actions.clearDevices();

export const setDeviceConnectionStatus = (dusn, status) =>
    iotDevicesSlice.actions.setDeviceConnectionStatus({dusn, status});

export const setIsDeviceAssetsReceived = (dusn, value) =>
    iotDevicesSlice.actions.setIsDeviceAssetsReceived({dusn, value});

export const setIsDeviceAssetsInProgress = (dusn, value) =>
    iotDevicesSlice.actions.setIsDeviceAssetsInProgress({dusn, value});

export const setIsDeviceConnectInProgress = (dusn, value) =>
    iotDevicesSlice.actions.setIsDeviceConnectInProgress({dusn, value});

export const setIsDeviceUpdateSettingInProgress = (dusn, settingName, value) =>
    iotDevicesSlice.actions.setIsDeviceUpdateSettingInProgress({dusn, settingName, value});

export const setIsDeviceSyncInProgress = (dusn, value) =>
    iotDevicesSlice.actions.setIsDeviceSyncInProgress({dusn, value});

export const setIsDeviceSyncIsFailed = (dusn, value) => iotDevicesSlice.actions.setIsDeviceSyncIsFailed({dusn, value});

export const setIsDeviceSyncStarted = (dusn, value) => iotDevicesSlice.actions.setIsDeviceSyncStarted({dusn, value});

export const setIsDeviceConnectIsFailed = (dusn, value) =>
    iotDevicesSlice.actions.setIsDeviceConnectIsFailed({dusn, value});

export const setIsDeviceInitialized = (dusn, value) => iotDevicesSlice.actions.setIsDeviceInitialized({dusn, value});

export const setIsDeviceReconnectInProgress = (dusn, value) =>
    iotDevicesSlice.actions.setIsDeviceReconnectInProgress({dusn, value});

export const setDeviceAuthToken = (dusn, value) => iotDevicesSlice.actions.setDeviceAuthToken({dusn, value});

export const setCurrentDusn = (dusn) => iotDevicesSlice.actions.setCurrentDusn(dusn);

export const autoSelectCurrentDusn = (dusn) => iotDevicesSlice.actions.autoSelectCurrentDusn(dusn);

export const setLtePairingError = (error) => iotDevicesSlice.actions.setLtePairingError(error);

export const resetLtePairingError = () => iotDevicesSlice.actions.setLtePairingError('');

export const setDeviceADData = (dusn, data) => updateDeviceData(dusn, {advancedDiagnostic: data});

export const setDeviceUsageData = (dusn, data) => updateDeviceData(dusn, {usage: data});

export const setDeviceUsageStickType = (dusn, stickType) => updateDeviceData(dusn, {usage: {stickType}});

export const setHolderLastChargerDusn = (holderDusn, chargerDusn) => (dispatch) => {
    dispatch(updateDeviceData(holderDusn, {lastChargerDusn: chargerDusn}));
    dispatch(updateDeviceData(chargerDusn, {holderDusn}));
};

export const setIsExperienceDataLoading = (dusn, value) => updateDeviceData(dusn, {isExperienceDataLoading: value});

export const setExperienceInferenceData = (dusn, data) =>
    iotDevicesSlice.actions.setExperienceInferenceData({dusn, data});

export const setExperienceCalibrationData = (dusn, data) =>
    iotDevicesSlice.actions.setExperienceCalibrationData({dusn, data});

export default iotDevicesSlice.reducer;
